/**
 * Hook that persists the latest questionnaire-review record for a single PA
 * order via {@link putLatestQuestionnaireWithResponse}.
 *
 * The hook exposes an imperative trigger plus the full loading lifecycle
 * (`idle` → `saving` → `success` / `error`) so consumers can drive UI states
 * such as disabling a submit button, showing a spinner, or flashing a toast.
 * Built on top of {@link useAbortableFetch} so any in-flight PUT is cancelled
 * automatically on a follow-up call or when the consuming component
 * unmounts.
 *
 * @module pharmaQuestionaire/hooks/usePharmaQuestionnaireUpdate
 */
import { useCallback, useEffect, useState } from "react";
import { putLatestQuestionnaireWithResponse } from "../../../api/putCalls/putLatestQuestionnaireWithResponse";
import {
  UpdateQuestionnaireReviewRequest,
  UpdateQuestionnaireReviewResponse,
} from "../../../data-model/questionnaireReview";
import { useAbortableFetch } from "../../../hooks/useAbortableFetch";

/** Lifecycle phases the consumer can branch UI on. */
export type QuestionnaireUpdateStatus = "idle" | "saving" | "success" | "error";

/** Shape returned to consumers of {@link usePharmaQuestionnaireUpdate}. */
interface UsePharmaQuestionnaireUpdateReturn {
  /** Parsed envelope from the most recent successful PUT, or `null`. */
  data: UpdateQuestionnaireReviewResponse | null;
  /** Coarse status flag that mirrors the request lifecycle. */
  status: QuestionnaireUpdateStatus;
  /** `true` while a PUT request is in flight. */
  saving: boolean;
  /** `true` once a PUT has resolved successfully since the last reset. */
  isSuccess: boolean;
  /** `true` once a PUT has failed since the last reset. */
  isError: boolean;
  /** Last non-abort error from the API, or `null`. */
  error: Error | null;
  /**
   * Triggers the PUT for the supplied `orderId` / `body`. Aborts any previous
   * in-flight request before starting a new one. Resolves to the parsed
   * envelope on success, or `null` on failure / cancellation.
   */
  updateQuestionnaireReview: (
    orderId: string,
    body: UpdateQuestionnaireReviewRequest,
  ) => Promise<UpdateQuestionnaireReviewResponse | null>;
  /** Aborts any in-flight PUT. */
  abort: () => void;
  /** Resets `status`, `data`, and `error` back to their initial values. */
  reset: () => void;
}

/**
 * Returns an imperative updater plus the latest data / loading / error state
 * for the questionnaire-review PUT endpoint. The hook does NOT fire
 * automatically — consumers must call
 * `updateQuestionnaireReview(orderId, body)`.
 *
 * @returns Imperative updater and request state
 */
export const usePharmaQuestionnaireUpdate =
  (): UsePharmaQuestionnaireUpdateReturn => {
    const putReview = useCallback(
      (
        signal: AbortSignal,
        orderId: string,
        body: UpdateQuestionnaireReviewRequest,
      ) => putLatestQuestionnaireWithResponse(orderId, body, signal),
      [],
    );

    const { runFetch, abort, data, error, loading } =
      useAbortableFetch<UpdateQuestionnaireReviewResponse>(putReview);

    const [status, setStatus] = useState<QuestionnaireUpdateStatus>("idle");

    const updateQuestionnaireReview = useCallback(
      async (
        orderId: string,
        body: UpdateQuestionnaireReviewRequest,
      ): Promise<UpdateQuestionnaireReviewResponse | null> => {
        if (!orderId) return null;
        setStatus("saving");
        const result = await runFetch(orderId, body);
        if (result.success) {
          setStatus("success");
          return result.data;
        }
        if ((result.error as Error | undefined)?.name === "AbortError") {
          setStatus("idle");
        } else {
          setStatus("error");
        }
        return null;
      },
      [runFetch],
    );

    const reset = useCallback(() => {
      abort();
      setStatus("idle");
    }, [abort]);

    useEffect(() => () => abort(), [abort]);

    const normalizedError =
      error && error.name !== "AbortError"
        ? error instanceof Error
          ? error
          : new Error(String(error))
        : null;

    return {
      data,
      status,
      saving: loading,
      isSuccess: status === "success",
      isError: status === "error",
      error: normalizedError,
      updateQuestionnaireReview,
      abort,
      reset,
    };
  };
